import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import '../styles/Teams.css';
import { JOB_TITLES } from '../constants/jobTitles';
import { EMPLOYEE_DOCUMENT_TYPES } from '../constants/documentTypes';

const API_BASE = API_BASE_URL;

const EMPTY_EMPLOYEE_FORM = {
  user_id: '',
  position: '',
  custom_position: '',
  name: '',
  email: '',
  phone: '',
  contact_info: '',
};

const Teams = () => {
  const { user, permissions } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showTeamForm, setShowForm] = useState(false);
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedEmployeeForDocs, setSelectedEmployeeForDocs] = useState(null);
  const [employeeDocs, setEmployeeDocs] = useState([]);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const [newTeam, setNewTeam] = useState({ name: '', type: 'internal' });
  const [newEmp, setNewEmp] = useState(EMPTY_EMPLOYEE_FORM);
  const [docForm, setDocForm] = useState({ document_type: '', custom_document_type: '', file: null });

  const canManageTeams = hasEffectivePermission(user, permissions, 'can_manage_teams');
  const canManageDocuments = hasEffectivePermission(user, permissions, 'can_manage_documents');

  const fetchTeams = useCallback(async () => {
    const res = await apiClient.get(`${API_BASE}/teams`);
    const rows = Array.isArray(res.data) ? res.data : [];
    setTeams(rows);
    if (selectedTeam) {
      const refreshed = rows.find((team) => Number(team.id) === Number(selectedTeam.id));
      setSelectedTeam(refreshed || null);
    }
  }, [selectedTeam]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiClient.get(`${API_BASE}/users/options`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      try {
        const fallback = await apiClient.get(`${API_BASE}/users`);
        setUsers(Array.isArray(fallback.data) ? fallback.data : []);
      } catch (innerErr) {
        setUsers([]);
      }
    }
  }, []);

  const fetchEmployees = useCallback(async (teamId) => {
    const res = await apiClient.get(`${API_BASE}/teams/${teamId}/employees`);
    setEmployees(Array.isArray(res.data) ? res.data : []);
  }, []);

  const fetchEmployeeDocuments = async (employeeId) => {
    const res = await apiClient.get(`${API_BASE}/teams/employees/${employeeId}/documents`);
    setEmployeeDocs(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    Promise.all([fetchTeams(), fetchUsers()]);
  }, [fetchTeams, fetchUsers]);

  useEffect(() => {
    if (selectedTeam) {
      fetchEmployees(selectedTeam.id);
    }
  }, [selectedTeam, fetchEmployees]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!canManageTeams) return;
    try {
      await apiClient.post(`${API_BASE}/teams`, newTeam);
      setShowForm(false);
      setNewTeam({ name: '', type: 'internal' });
      await fetchTeams();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Ekip olu?turulamad?');
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    if (!canManageTeams) return;
    if (!editingTeam?.id) return;
    try {
      await apiClient.put(`${API_BASE}/teams/${editingTeam.id}`, editingTeam);
      setEditingTeam(null);
      await fetchTeams();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Ekip g?ncellenemedi');
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!canManageTeams) return;
    if (!window.confirm(`${team.name} ekibini silmek istiyor musunuz?`)) return;
    try {
      await apiClient.delete(`${API_BASE}/teams/${team.id}`);
      if (Number(selectedTeam?.id) === Number(team.id)) {
        setSelectedTeam(null);
        setEmployees([]);
      }
      await fetchTeams();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Ekip silinemedi');
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (!canManageTeams) return;
    try {
      const resolvedPosition = newEmp.position === 'Diğer' ? newEmp.custom_position : newEmp.position;
      await apiClient.post(
        `${API_BASE}/teams/employees`,
        {
          ...newEmp,
          position: resolvedPosition || null,
          user_id: newEmp.user_id ? parseInt(newEmp.user_id, 10) : null,
          team_id: selectedTeam.id,
          contact_info: newEmp.email || newEmp.phone || newEmp.contact_info || null,
        },
      );
      setShowEmpForm(false);
      setNewEmp(EMPTY_EMPLOYEE_FORM);
      await fetchEmployees(selectedTeam.id);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Personel eklenemedi');
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    if (!canManageTeams) return;
    if (!editingEmployee?.id) return;
    try {
      const resolvedPosition = editingEmployee.position === 'Diğer' ? editingEmployee.custom_position : editingEmployee.position;
      await apiClient.put(
        `${API_BASE}/teams/employees/${editingEmployee.id}`,
        {
          user_id: editingEmployee.user_id ? Number(editingEmployee.user_id) : null,
          name: editingEmployee.name,
          position: resolvedPosition || null,
          email: editingEmployee.email || null,
          phone: editingEmployee.phone || null,
          contact_info: editingEmployee.email || editingEmployee.phone || editingEmployee.contact_info || null,
        },
      );
      setEditingEmployee(null);
      if (selectedTeam?.id) {
        await fetchEmployees(selectedTeam.id);
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Personel g?ncellenemedi');
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!canManageTeams) return;
    if (!window.confirm(`${employee.name} kayd?n? silmek istiyor musunuz?`)) return;
    try {
      await apiClient.delete(`${API_BASE}/teams/employees/${employee.id}`);
      if (selectedTeam?.id) {
        await fetchEmployees(selectedTeam.id);
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Personel silinemedi');
    }
  };

  const handleLinkedUserChange = (userId) => {
    if (!userId) {
      setNewEmp((prev) => ({ ...prev, user_id: '', name: '', email: '', phone: '', contact_info: '' }));
      return;
    }
    const selectedUser = users.find((user) => Number(user.id) === Number(userId));
    if (!selectedUser) return;
    setNewEmp((prev) => ({
      ...prev,
      user_id: String(userId),
      name: selectedUser.username || prev.name,
      email: selectedUser.email || prev.email,
      phone: selectedUser.phone_number || prev.phone,
      contact_info: selectedUser.email || selectedUser.phone_number || prev.contact_info,
      position: selectedUser.job_title || prev.position,
    }));
  };

  const handleEditUserChange = (userId) => {
    if (!editingEmployee) return;
    if (!userId) {
      setEditingEmployee((prev) => ({ ...prev, user_id: '', name: '', email: '', phone: '', contact_info: '' }));
      return;
    }
    const selectedUser = users.find((user) => Number(user.id) === Number(userId));
    if (!selectedUser) return;
    setEditingEmployee((prev) => ({
      ...prev,
      user_id: String(userId),
      name: selectedUser.username || prev.name,
      email: selectedUser.email || prev.email,
      phone: selectedUser.phone_number || prev.phone,
      contact_info: selectedUser.email || selectedUser.phone_number || prev.contact_info,
      position: selectedUser.job_title || prev.position,
    }));
  };

  const openDocumentsModal = async (employee) => {
    if (!canManageDocuments) return;
    setSelectedEmployeeForDocs(employee);
    setDocForm({ document_type: '', custom_document_type: '', file: null });
    setShowDocModal(true);
    await fetchEmployeeDocuments(employee.id);
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!canManageDocuments) return;
    if (!selectedEmployeeForDocs?.id) return;
    if (!docForm.file) {
      window.alert('L?tfen dosya se?in');
      return;
    }

    try {
      const form = new FormData();
      const resolvedDocType = docForm.document_type === 'Diğer' ? docForm.custom_document_type : docForm.document_type;
      form.append('document', docForm.file);
      form.append('document_type', resolvedDocType || 'Diğer');

      await apiClient.post(`${API_BASE}/teams/employees/${selectedEmployeeForDocs.id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocForm({ document_type: '', custom_document_type: '', file: null });
      await fetchEmployeeDocuments(selectedEmployeeForDocs.id);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Belge y?klenemedi');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await apiClient.get(`${API_BASE}/teams/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_path?.split('/').pop() || `document-${doc.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('Belge indirilemedi');
    }
  };

  return (
    <div className="teams-container">
      <div className="teams-sidebar">
        <div className="sidebar-header-teams">
          <h3>Ekipler ve Alt Yükleniciler</h3>
          {canManageTeams && (
            <button onClick={() => setShowForm(true)} className="btn-add" type="button" aria-label="Ekip ekle">
              <span className="btn-add-icon">+</span>
            </button>
          )}
        </div>
        <div className="teams-list">
          {teams.map((team) => (
            <div
              key={team.id}
              className={`team-item ${selectedTeam?.id === team.id ? 'active' : ''}`}
              onClick={() => setSelectedTeam(team)}
            >
              <span className={`team-icon ${team.type === 'internal' ? 'internal' : 'subcontractor'}`}>
                {team.type === 'internal' ? 'IC' : 'AL'}
              </span>
              <span className="team-item-name">{team.name}</span>
              {canManageTeams && (
                <div className="team-item-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-icon" type="button" onClick={() => setEditingTeam({ ...team })}>Düzenle</button>
                  <button className="btn-icon danger" type="button" onClick={() => handleDeleteTeam(team)}>Sil</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="teams-content">
        {selectedTeam ? (
          <>
            <div className="content-header">
              <h2>
                {selectedTeam.name}{' '}
                <span className="team-badge">{selectedTeam.type === 'internal' ? 'İç Ekip' : 'Alt Yüklenici'}</span>
              </h2>
              {canManageTeams && (
                <button onClick={() => setShowEmpForm(true)} className="btn-primary" type="button">Personel Ekle</button>
              )}
            </div>

            <div className="employees-grid">
              {employees.length === 0 ? (
                <p className="no-data">Bu ekipte henüz personel yok.</p>
              ) : (
                employees.map((employee) => (
                  <div key={employee.id} className="employee-card">
                    <div className="emp-avatar">{(employee.name || '?').charAt(0)}</div>
                    <div className="emp-info">
                      <h4>{employee.name}</h4>
                      <p>{employee.position || '-'}</p>
                      <small>E-posta: {employee.email || employee.user_email || '-'}</small>
                      <small style={{ display: 'block' }}>Telefon: {employee.phone || employee.user_phone || '-'}</small>
                      {employee.user_username && (
                        <small style={{ display: 'block' }}>Hesap: {employee.user_username}</small>
                      )}
                    </div>
                    <div className="emp-actions">
                      {canManageDocuments && (
                        <button className="btn-icon" type="button" title="Belge Yönetimi" onClick={() => openDocumentsModal(employee)}>DOC</button>
                      )}
                      {canManageTeams && (
                        <>
                          <button
                            className="btn-icon"
                            type="button"
                            onClick={() =>
                              setEditingEmployee({
                                ...employee,
                                user_id: employee.user_id ? String(employee.user_id) : '',
                                email: employee.email || employee.user_email || '',
                                phone: employee.phone || employee.user_phone || '',
                                custom_position: '',
                              })
                            }
                          >
                            Düzenle
                          </button>
                          <button className="btn-icon danger" type="button" onClick={() => handleDeleteEmployee(employee)}>Sil</button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="empty-selection">
            <p>Soldan bir ekip seçerek personel listesini görebilirsiniz.</p>
          </div>
        )}
      </div>

      {showTeamForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Yeni Ekip Oluştur</h3>
            <form onSubmit={handleCreateTeam}>
              <input type="text" placeholder="Ekip Adı" className="form-control" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} required />
              <select className="form-control" value={newTeam.type} onChange={(e) => setNewTeam({ ...newTeam, type: e.target.value })}>
                <option value="internal">İç Ekip</option>
                <option value="subcontractor">Alt Yüklenici</option>
              </select>
              <div className="form-actions">
                <button type="submit" className="btn-submit">Kaydet</button>
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingTeam && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Ekip Düzenle</h3>
            <form onSubmit={handleUpdateTeam}>
              <input type="text" className="form-control" value={editingTeam.name} onChange={(e) => setEditingTeam((prev) => ({ ...prev, name: e.target.value }))} required />
              <select className="form-control" value={editingTeam.type} onChange={(e) => setEditingTeam((prev) => ({ ...prev, type: e.target.value }))}>
                <option value="internal">İç Ekip</option>
                <option value="subcontractor">Alt Yüklenici</option>
              </select>
              <div className="form-actions">
                <button type="submit" className="btn-submit">Güncelle</button>
                <button type="button" className="btn-cancel" onClick={() => setEditingTeam(null)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEmpForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Yeni Personel Ekle</h3>
            <form onSubmit={handleCreateEmployee}>
              <select className="form-control" value={newEmp.user_id} onChange={(e) => handleLinkedUserChange(e.target.value)}>
                <option value="">Sistem kullanıcısı bağlama (opsiyonel)</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                ))}
              </select>

              <input type="text" placeholder="Ad Soyad" className="form-control" value={newEmp.name} onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })} required />

              <select className="form-control" value={newEmp.position} onChange={(e) => setNewEmp({ ...newEmp, position: e.target.value, custom_position: '' })} required>
                <option value="">Görev Seçiniz</option>
                {JOB_TITLES.map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>

              {newEmp.position === 'Diğer' && (
                <input type="text" placeholder="Görevi giriniz" className="form-control" value={newEmp.custom_position} onChange={(e) => setNewEmp({ ...newEmp, custom_position: e.target.value })} required />
              )}

              <input type="email" placeholder="E-posta" className="form-control" value={newEmp.email} onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value, contact_info: e.target.value || newEmp.phone })} />
              <input type="text" placeholder="Telefon" className="form-control" value={newEmp.phone} onChange={(e) => setNewEmp({ ...newEmp, phone: e.target.value, contact_info: newEmp.email || e.target.value })} />

              <div className="form-actions">
                <button type="submit" className="btn-submit">Kaydet</button>
                <button type="button" className="btn-cancel" onClick={() => setShowEmpForm(false)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingEmployee && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Personel Düzenle</h3>
            <form onSubmit={handleUpdateEmployee}>
              <select className="form-control" value={editingEmployee.user_id || ''} onChange={(e) => handleEditUserChange(e.target.value)}>
                <option value="">Sistem kullanıcısı bağlama (opsiyonel)</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                ))}
              </select>

              <input type="text" className="form-control" value={editingEmployee.name || ''} onChange={(e) => setEditingEmployee((prev) => ({ ...prev, name: e.target.value }))} required />

              <select className="form-control" value={editingEmployee.position || ''} onChange={(e) => setEditingEmployee((prev) => ({ ...prev, position: e.target.value, custom_position: '' }))} required>
                <option value="">Görev Seçiniz</option>
                {JOB_TITLES.map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>

              {editingEmployee.position === 'Diğer' && (
                <input type="text" className="form-control" placeholder="Görevi giriniz" value={editingEmployee.custom_position || ''} onChange={(e) => setEditingEmployee((prev) => ({ ...prev, custom_position: e.target.value }))} required />
              )}

              <input type="email" className="form-control" placeholder="E-posta" value={editingEmployee.email || ''} onChange={(e) => setEditingEmployee((prev) => ({ ...prev, email: e.target.value, contact_info: e.target.value || prev.phone || '' }))} />
              <input type="text" className="form-control" placeholder="Telefon" value={editingEmployee.phone || ''} onChange={(e) => setEditingEmployee((prev) => ({ ...prev, phone: e.target.value, contact_info: prev.email || e.target.value || '' }))} />

              <div className="form-actions">
                <button type="submit" className="btn-submit">Güncelle</button>
                <button type="button" className="btn-cancel" onClick={() => setEditingEmployee(null)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDocModal && selectedEmployeeForDocs && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedEmployeeForDocs.name} - Belge Yönetimi</h3>

            <form onSubmit={handleUploadDocument}>
              <select className="form-control" value={docForm.document_type} onChange={(e) => setDocForm((prev) => ({ ...prev, document_type: e.target.value, custom_document_type: '' }))} required>
                <option value="">Belge tipi seçiniz</option>
                {EMPLOYEE_DOCUMENT_TYPES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>

              {docForm.document_type === 'Diğer' && (
                <input className="form-control" type="text" placeholder="Belge tipini girin" value={docForm.custom_document_type} onChange={(e) => setDocForm((prev) => ({ ...prev, custom_document_type: e.target.value }))} required />
              )}

              <input type="file" className="form-control" onChange={(e) => setDocForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))} required />

              <div className="form-actions">
                <button type="submit" className="btn-submit">Belge Yükle</button>
                <button type="button" className="btn-cancel" onClick={() => setShowDocModal(false)}>Kapat</button>
              </div>
            </form>

            <div style={{ marginTop: '1rem' }}>
              <h4>Yüklenen Belgeler</h4>
              {employeeDocs.length === 0 ? (
                <small>Belge bulunamadı.</small>
              ) : (
                <div style={{ display: 'grid', gap: '0.4rem' }}>
                  {employeeDocs.map((doc) => (
                    <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e6eef8', borderRadius: 8, padding: '0.45rem 0.6rem' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{doc.document_type || 'Belge'}</div>
                        <small>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('tr-TR') : '-'}</small>
                      </div>
                      <button type="button" className="btn-icon" onClick={() => handleDownloadDocument(doc)}>İndir</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;



